import { computed, ref } from "vue";
import { useClipboard } from "@vueuse/core";
import { useToast } from "primevue/usetoast";

import { useBonusReportsStore } from "@/stores/BonusReports";
import { useUserStore } from "@/stores/User";
import routeManager from "@/router/route-manager";

import { Article, ContentfulItem, Webinar } from "@tmi/types/entities";
import { articleKey, bonusReportKey, tradeAlertKey, webinarKey } from "@/types/products/product-definitions";

export function useContentActions() {
    const { copy, isSupported, copied, text } = useClipboard();

    const bonusReportsStore = useBonusReportsStore();
    const userStore = useUserStore();

    function isFavoritable( item: ContentfulItem | undefined ): item is Article | Webinar {
        return ( item as Article ) !== undefined || ( item as Webinar ) !== undefined;
    }

    async function handleFavoritedContentAction( contentfulId: string | undefined, contentType: string | undefined ) {
        if ( !( contentfulId && contentType ) ) {
            return;
        }

        userStore.toggleContentFavoriteStatus( contentfulId, contentType, ( result: boolean ) => {
            if ( result ) {
                if ( userStore.isFavorited( contentfulId, contentType ) ) {
                    showFavoritedToast( contentType );
                } else {
                    showDefavoritedToast( contentType );
                }
            } else {
                showFavoritedActionFailureToast( contentType );
            }
        } );

        return null;
    }

    const showCopiedToast = ( link: string ) => toaster.add( {
        severity: 'success',
        detail: `Copied '${ link }' to the clipboard`,
        life: 3000
    } );

    const showFailedToCopyToast = () => toaster.add( {
        severity: 'error',
        detail: 'Unable to access the system clipboard. Please verify that the web site has been granted access and try again. If this continues to happen, you can open the article in a new tab by clicking the other button and then either save the article to your reading list or copy the url from the browser.',
        life: 3000
    } );


    function handleOpenContentInNewTab( content: ContentfulItem | undefined, contentType: string | undefined ): void {
        if ( !content ) {
            return;
        }

        let link: string | undefined;
        switch ( contentType ) {
            case articleKey:
                link = routeManager.articleRouteBySlug( content.slug );
                break;
            case bonusReportKey:
                link = content.contentfulId ? bonusReportsStore.bonusReportById( content.contentfulId )?.pdfLink : undefined;
                break;
            case tradeAlertKey:
                link = routeManager.tradeAlertRouteBySlug( content.slug );
                break;
            case webinarKey:
                link = routeManager.webinarRouteBySlug( content.slug );
                break;
            default:
                link = undefined;
                break;
        }

        if ( link ) {
            window.open( link, '_blank' );
        }
    }

    async function handleCopyContentLink( content: ContentfulItem | undefined, contentType: string | undefined ): Promise< void > {
        if ( !( content && contentType ) ) {
            return;
        }

        if ( isSupported ) {
            let contentRoute: string;
            if ( articleKey === contentType ) {
                contentRoute = routeManager.articleRouteBySlug( content.slug );
            } else if ( webinarKey === contentType ) {
                contentRoute = routeManager.webinarRouteBySlug( content.slug );
            } else {
                contentRoute = routeManager.tradeAlertRouteBySlug( content.slug );
            }

            const contentLink = `${ location.host }${ contentRoute }`;
            await copy( contentLink );

            if ( copied ) {
                showCopiedToast( text.value );
            } else {
                showFailedToCopyToast();
            }
        }
    }

    const dataTipForOpenContentInNewTab = ref< string >( 'Open in new tab' );
    const dataTipForCopyLink = ref< string >( 'Copy link' );

    function dataTipForFavoritedStatus( contentfulId: string | undefined, contentType: string | undefined ): string {
        if ( isFavorited( contentfulId, contentType ) ) {
            return 'Remove from library';
        }

        return 'Add to library';
    }

    function favoriteIconClassNameForContent( contentfulId: string | undefined, contentType: string | undefined ): string {
        return isFavorited( contentfulId, contentType ) ? 'pi-bookmark-fill' : 'pi-bookmark';
    }

    function isFavorited( contentfulId: string | undefined, contentType: string | undefined ): boolean {
        let isFavoriteContent = false;
        if ( contentfulId && contentType ) {
            isFavoriteContent = userStore.isFavorited( contentfulId, contentType );
        }

        return isFavoriteContent;
    }

    const showCopyAction = computed< boolean >( () => isSupported.value );

    const toaster = useToast();
    const showFavoritedToast = ( contentType: string ) => toaster.add({ severity: 'info', detail: `The ${ contentType } has been added to your favorite ${ contentType }s list.`, life: 3000 });
    const showDefavoritedToast = ( contentType: string ) => toaster.add({ severity: 'info', detail: `The ${ contentType } has been removed from your favorite ${ contentType }s list.`, life: 3000 });
    const showFavoritedActionFailureToast = ( contentType: string ) => toaster.add({ severity: 'info', detail: `The ${ contentType } status failed to update. Please try again later.`, life: 3000 });

    return {
        dataTipForCopyLink,
        dataTipForFavoritedStatus,
        dataTipForOpenContentInNewTab,
        favoriteIconClassNameForContent,
        handleCopyContentLink,
        handleFavoritedContentAction,
        handleOpenContentInNewTab,
        isFavoritable,
        isFavorited,
        showCopyAction
    };
}
